import { cn } from "@/lib/utils";
import { ReactNode } from "react";

interface WindowHeaderProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function WindowHeader({ title, subtitle, icon, actions, className }: WindowHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between p-6 border-b border-white/10", className)}>
      <div className="flex items-center space-x-3">
        {icon && <div className="text-primary">{icon}</div>}
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">{title}</h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1 font-medium">{subtitle}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center space-x-3">{actions}</div>}
    </div>
  );
}

interface WindowContentProps {
  children: ReactNode;
  className?: string;
  padding?: boolean;
}

export function WindowContent({ children, className, padding = true }: WindowContentProps) {
  return (
    <div className={cn(
      "flex-1 overflow-auto",
      padding && "p-6",
      className
    )}>
      {children}
    </div>
  );
}

interface WindowGridProps {
  children: ReactNode;
  className?: string;
  cols?: number;
}

export function WindowGrid({ children, className, cols = 3 }: WindowGridProps) {
  return (
    <div className={cn(
      "grid gap-6",
      cols === 2 && "lg:grid-cols-2",
      cols === 3 && "lg:grid-cols-3", 
      cols === 4 && "lg:grid-cols-4",
      className
    )}>
      {children}
    </div>
  );
}

interface WindowSidebarProps {
  children: ReactNode;
  className?: string;
}

export function WindowSidebar({ children, className }: WindowSidebarProps) {
  return (
    <div className={cn("space-y-6", className)}>
      {children}
    </div>
  );
}

interface WindowSectionProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'strong' | 'ultra';
}

export function WindowSection({ title, icon, children, className, variant = 'default' }: WindowSectionProps) {
  return (
    <div className={cn(
      "glass rounded-xl p-6 transition-all duration-300 hover:shadow-lg",
      variant === 'strong' && "glass-strong",
      variant === 'ultra' && "glass-ultra",
      className
    )}>
      <div className="flex items-center space-x-2 mb-4">
        {icon && <div className="text-primary">{icon}</div>}
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      </div>
      {children}
    </div>
  );
}

interface WindowMetricProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
}

export function WindowMetric({ label, value, icon, variant = 'default', className }: WindowMetricProps) {
  return (
    <div className={cn(
      "flex items-center justify-between p-3 rounded-lg transition-colors",
      variant === 'success' && "bg-success/10 border border-success/20",
      variant === 'warning' && "bg-warning/10 border border-warning/20", 
      variant === 'danger' && "bg-danger/10 border border-danger/20",
      variant === 'default' && "bg-white/5 border border-white/10",
      className
    )}>
      <div className="flex items-center space-x-2">
        {icon && <div className={cn(
          "text-sm",
          variant === 'success' && "text-success",
          variant === 'warning' && "text-warning",
          variant === 'danger' && "text-danger",
          variant === 'default' && "text-muted-foreground"
        )}>{icon}</div>}
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
      </div>
      <span className={cn(
        "text-sm font-semibold",
        variant === 'success' && "text-success",
        variant === 'warning' && "text-warning",
        variant === 'danger' && "text-danger", 
        variant === 'default' && "text-foreground"
      )}>
        {value}
      </span>
    </div>
  );
}

interface WindowStatsProps {
  children: ReactNode;
  className?: string;
}

export function WindowStats({ children, className }: WindowStatsProps) {
  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", className)}>
      {children}
    </div>
  );
}